<script setup lang="ts">
// import * as Sentry from "@sentry/vue";
import * as Sentry from "@sentry/vue";
import { useProductPageStore } from "~/stores/main";
import ProductCarousel from "~/components/Carousel/ProductCarousel.vue";

/*
definePageMeta({
  middleware: [
    async function (to) {
      try {
        const masterProduct = await useMeilisearch(
          String(useRuntimeConfig().public.meilisearchIndexMasterproducts),
          {
            filter: ["sku = " + to.params.sku],
          },
        );
        if (Array.isArray(masterProduct) && masterProduct.length === 0) {
          throw createError({
            statusCode: 404,
          });
        }
      } catch (error) {
        Sentry.captureEvent({
          message: `An error occured when fetching master product (${to.params.sku})`,
          level: "error",
          extra: {
            error,
          },
        });
      }
    },
  ],
});
*/

// -----------------------
// composables
// -----------------------
const route = useRoute();
const config = useRuntimeConfig();
const store = useProductPageStore();
const { getVariantByProperty, createMetaDescription } = useHelpers();
const { productPageUrl } = useHelpers();

const { $api } = useNuxtApp();
const settingsWebshop = await $api(`system/settings/modules/webshop`);

// -----------------------
// reactive properties
// -----------------------
const productFoundRef = ref<boolean>(true);
const similarProducts = ref([]);

// -----------------------
// product load logic
// -----------------------
let masterProduct;

try {
  masterProduct = await $fetch(`/api/meilisearch/masterproducts`, {
    method: "POST",
    body: {
      sku: route.params.sku,
    },
  });
} catch (error) {
  Sentry.captureEvent({
    message: `An error occured when fetching master product`,
    level: "error",
    extra: {
      sku: route.params.sku,
      error,
    },
  });
}

if (masterProduct) {
  store.masterProduct = masterProduct;
  const products = store.masterProduct.products;
  let defaultSize = null;

  // Fetch similar products for SSR
  try {
    similarProducts.value = await $fetch(`/api/meilisearch/similar-products`, {
      method: "POST",
      body: {
        id: store.masterProduct.id,
        embedder: "products-openai",
        limit: 10,
      },
    });
  } catch (error) {
    Sentry.captureEvent({
      message: `Failed to fetch similar products`,
      level: "error",
      extra: {
        sku: route.params.sku,
        error,
      },
    });
    similarProducts.value = [];
  }

  if ("size" in route.query) {
    const size = decodeURIComponent(route.query.size);
    const variantWithSize = getVariantByProperty(
      store.masterProduct,
      "product_meret_property",
      size,
    );

    if (variantWithSize) {
      defaultSize = size;
    }
  }

  if (defaultSize === null) {
    const sizes = store.masterProduct.products
      .filter((product) => product.product.stock > 0) // only include products with stock
      .map((data: Record<any, any>) => data.product.product_meret_property) // Collect all sizes
      .filter(
        (size: string, i: number, self: string[]) => self.indexOf(size) === i,
      ); // Make the list unique

    if (sizes.length === 1) {
      defaultSize = sizes[0];
    }
  }

  if (defaultSize !== null) {
    store.size = defaultSize;
  }

  // fetch manufacturer data for logo
  if (store.masterProduct?.masterproduct_gyarto_property) {
    try {
      const manufacturers = await $fetch(`/api/meilisearch/manufacturers`, {
        method: "POST",
        body: {
          name: store.masterProduct.masterproduct_gyarto_property,
        },
      });

      if (Array.isArray(manufacturers) && manufacturers.length > 0) {
        store.manufacturer = manufacturers[0];
      }
    } catch (error) {
      Sentry.captureEvent({
        message: `Failed to fetch manufacturers`,
        level: "error",
        extra: {
          key: "ER-NU6V",
          name: store.masterProduct.masterproduct_gyarto_property,
          error,
        },
      });
    }
  }

  const metaTitle = () => {
    let title = store.masterProduct?.full_name;
    title += ' | ' + store.masterProduct?.masterproduct_nem_property  + ' ' + store.masterProduct?.calculated_cmspage_breadcrumb?.at(-1)?.label?.toLowerCase() + ' | ';
    title += config.public.theme.group === "tnf" ? "The North Face Shop" : "Mountex";

    return title;
  }

  // -----------------------
  // Schema.org product data
  // -----------------------

  function removeNulls(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(removeNulls).filter((v) => v != null);
    } else if (obj !== null && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj)
          .filter(([_, v]) => v !== null && v !== undefined)
          .map(([k, v]) => [k, removeNulls(v)]),
      );
    }
    return obj;
  }

  const soOffers = [];
  for (const soOffer of products) {

    let soOfferPrice = parseInt(soOffer.product.price);
    if (
      settingsWebshop && 
      settingsWebshop.global_percent_coupon_calculation_enabled && 
      Date.now() > settingsWebshop.global_percent_coupon_calculation_start_time &&
      Date.now() < settingsWebshop.global_percent_coupon_calculation_end_time &&
      parseInt(settingsWebshop.global_percent_coupon_calculation_discount_percent)>0
    ) {
      let applyIt = false;
      if (settingsWebshop.global_percent_coupon_calculation_only_inaction) {
        if (soOffer.product.price != soOffer.product.original_price) applyIt = true;
      } else {
        applyIt = true;
      }

      if (applyIt) {
        soOfferPrice = Math.round(soOffer.product.price * (100 - settingsWebshop.global_percent_coupon_calculation_discount_percent) / 100);
      }
    }

    soOffers.push({
      "@type": "Offer",
      price: soOfferPrice,
      sku: soOffer.product.sku,
      availability:
        soOffer.product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
    });
  }

  let soProductLowPrice = store.masterProduct?.calculated_base_prices.price_min;
    if (
      settingsWebshop && 
      settingsWebshop.global_percent_coupon_calculation_enabled && 
      Date.now() > settingsWebshop.global_percent_coupon_calculation_start_time &&
      Date.now() < settingsWebshop.global_percent_coupon_calculation_end_time &&
      parseInt(settingsWebshop.global_percent_coupon_calculation_discount_percent)>0
    ) {
      // console.log(store.masterProduct);
      let applyIt = false;
      if (settingsWebshop.global_percent_coupon_calculation_only_inaction) {
        if (store.masterProduct?.calculated_base_prices.in_action) applyIt = true;
      } else {
        applyIt = true;
      }

      if (applyIt) {
        soProductLowPrice = Math.round(store.masterProduct?.calculated_base_prices.price_min * (100 - settingsWebshop.global_percent_coupon_calculation_discount_percent) / 100);
      }
    }


  const soProductData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: store.masterProduct?.full_name,
    sku: store.masterProduct?.sku,
    brand: {
      "@type": "Brand",
      name: store.masterProduct?.masterproduct_gyarto_property,
    },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: useRuntimeConfig().public.currency,
      lowPrice: soProductLowPrice,
      highPrice: store.masterProduct?.calculated_base_prices.price_max,
      offerCount: soOffers.length,
      availability:
        store.masterProduct?.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      offers: soOffers,
    },
  };

  useHead({
    title: metaTitle(),
    meta: [
      {
        name: "description",
        content: createMetaDescription(store.masterProduct?.description),
      },
    ],
    bodyAttrs: {
      class: "product-page",
    },
    link: [
      {
        rel: "canonical",
        href: productPageUrl(store.masterProduct, false),
      },
    ],
  });

  useHead({
    script: [
      {
        type: "application/ld+json",
        children: JSON.stringify(removeNulls(soProductData)),
      },
    ],
  });
} else {
  throw createError({
    statusCode: 404,
  });
}
</script>

<template>
  <NuxtErrorBoundary>
    <template #default>
      <div v-if="productFoundRef">
        <ProductPageBreadcrumb></ProductPageBreadcrumb>
        <div class="lg:px-5">
          <div
            class="product-container grid grid-cols-2 gap-x-[20px] items-start p-2 lg:p-5 rounded-2xl bg-brand-surface mx-auto max-w-[1880px]"
          >
            <ProductPageLeft></ProductPageLeft>
            <ProductPageRight :settings-webshop="settingsWebshop"></ProductPageRight>
          </div>
        </div>

        <!-- Similar Products Section -->
        <div v-if="similarProducts.length > 0" class="mt-8 lg:px-5">
          <ProductCarousel
            method="filter"
            :title="$t('similar_products')"
            google-analytics-event="viewSimilarItem"
            :products="similarProducts"
          />
        </div>
      </div>
    </template>
  </NuxtErrorBoundary>
</template>
